const CACHE_PREFIX = "resultwale:admin-data:v1:";
const memoryCache = new Map();

const safeSessionStorage = () => {
  if (typeof window === "undefined") return null;

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

export const readAdminDataCache = (key, maxAgeMs = 10 * 60 * 1000) => {
  const now = Date.now();
  const memoryEntry = memoryCache.get(key);

  if (memoryEntry && now - memoryEntry.savedAt <= maxAgeMs) {
    return memoryEntry;
  }

  const storage = safeSessionStorage();
  if (!storage) return null;

  try {
    const raw = storage.getItem(`${CACHE_PREFIX}${key}`);
    if (!raw) return null;

    const entry = JSON.parse(raw);
    if (!entry || !Number.isFinite(entry.savedAt)) return null;

    if (now - entry.savedAt > maxAgeMs) {
      storage.removeItem(`${CACHE_PREFIX}${key}`);
      memoryCache.delete(key);
      return null;
    }

    memoryCache.set(key, entry);
    return entry;
  } catch {
    return null;
  }
};

export const writeAdminDataCache = (key, value) => {
  const entry = {
    value,
    savedAt: Date.now(),
    stale: false,
  };

  memoryCache.set(key, entry);

  const storage = safeSessionStorage();
  if (storage) {
    try {
      storage.setItem(`${CACHE_PREFIX}${key}`, JSON.stringify(entry));
    } catch {
      /* Cache is optional; quota/privacy mode must never break admin pages. */
    }
  }

  return entry;
};

export const removeAdminDataCache = (key) => {
  memoryCache.delete(key);

  const storage = safeSessionStorage();
  if (storage) {
    try {
      storage.removeItem(`${CACHE_PREFIX}${key}`);
    } catch {
      /* Ignore storage errors. */
    }
  }
};

export const clearAdminDataCache = () => {
  memoryCache.clear();

  const storage = safeSessionStorage();
  if (!storage) return;

  try {
    const keysToRemove = [];

    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key?.startsWith(CACHE_PREFIX)) keysToRemove.push(key);
    }

    keysToRemove.forEach((key) => storage.removeItem(key));
  } catch {
    /* Ignore storage errors. */
  }
};

export const markAdminDataCacheStale = () => {
  const storage = safeSessionStorage();

  for (const [key, entry] of memoryCache.entries()) {
    memoryCache.set(key, { ...entry, stale: true });
  }

  if (!storage) return;

  try {
    for (let index = 0; index < storage.length; index += 1) {
      const storageKey = storage.key(index);
      if (!storageKey?.startsWith(CACHE_PREFIX)) continue;

      const raw = storage.getItem(storageKey);
      if (!raw) continue;

      const entry = JSON.parse(raw);
      storage.setItem(storageKey, JSON.stringify({ ...entry, stale: true }));
    }
  } catch {
    /* Cache invalidation is best-effort only. */
  }
};
